import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { DevicesService } from "../../Services/Intune/devices.service";
import { SortDirection } from "@angular/material/sort";
import { IndexQueryParams, IndexResponse } from "../../Interfaces/api/Intune/devices.interface";
import { httpResource } from "@angular/common/http";
import { IntuneDevice } from "../../Models/Intune/intune-device.model";
import { firstValueFrom } from "rxjs";
import { DEFAULT_ORDER_BY, DEFAULT_ORDER_DIRECTION, DEFAULT_TOP, DEVICE_SELECT_FIELDS } from "../../Consts/Intune/list-devices.const";
import { ResourceStateService } from "../../Modules/Shared/Services/resource-state.service";

@Injectable()
export class ListDevicesFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _devicesService = inject(DevicesService);

    private readonly _searchTerm = signal('');
    private readonly _orderBy = signal(DEFAULT_ORDER_BY);
    private readonly _order = signal<SortDirection>(DEFAULT_ORDER_DIRECTION);
    private readonly _isLoadingMore = signal(false);

    private readonly _extraDevices = signal<IntuneDevice[]>([]);
    private readonly _nextLink = signal<string | null>(null);
    private readonly _count = signal<number | null>(null);

    private readonly _baseResource = httpResource<IndexResponse>(() => {
        const queryParams: IndexQueryParams = {
            search: this._searchTerm(),
            orderBy: this._orderBy(),
            order: this._order() as 'asc' | 'desc',
            top: DEFAULT_TOP,
            select: DEVICE_SELECT_FIELDS,
        }
        return this._devicesService.index(queryParams);
    });

    constructor() {
        this.initialize();
    }

    private initialize() {
        effect(() => {
            if (!this._baseResource.hasValue()) return;
            const value = this._resourceState.valueOf(this._baseResource);
            if (value?.data) {
                this._nextLink.set(value.data.next ?? null);
                this._count.set(value.data.count ?? null);
                this._extraDevices.set([]);
            }
        });
    }

    // ACTIONS
    public setSearchTerm(term: string) {
        this._searchTerm.set(term ?? '');
    }

    public setOrderBy(orderBy: string) {
        this._orderBy.set(orderBy ?? DEFAULT_ORDER_BY);
    }

    public setOrder(order: SortDirection) {
        this._order.set(order ?? DEFAULT_ORDER_DIRECTION);
    }

    public setSort(orderBy: string, order: SortDirection) {
        this.setOrderBy(orderBy);
        this.setOrder(order);
    }

    public async loadMore() {
        const next = this._nextLink();
        if (!next || this._isLoadingMore()) return;

        this._isLoadingMore.set(true);

        try {
            const response = await firstValueFrom(
                this._devicesService.next(next)
            );

            const newDevices = response.data.value.map((d) => new IntuneDevice(d));
            this._extraDevices.update((current) => [...current, ...newDevices]);

            this._nextLink.set(response.data.next ?? null);
            this._count.set(response.data.count ?? null);
        } finally {
            this._isLoadingMore.set(false);
        }
    }

    public reload() {
        this._nextLink.set(null);
        this._count.set(null);
        this._extraDevices.set([]);
        this._baseResource.reload();
    }

    public clear() {
        this._nextLink.set(null);
        this._count.set(null);
        this._extraDevices.set([]);
        this._searchTerm.set('');
    }

    // PUBLIC READ MODELS
    readonly devices = computed(() => {
        const baseDevices = this._baseResource.hasValue()
            ? (this._resourceState.valueOf(this._baseResource)?.data.value.map((d) => new IntuneDevice(d)) ?? [])
            : [];
        return [...baseDevices, ...this._extraDevices()];
    });

    readonly searchTerm = computed(() => this._searchTerm());
    readonly orderBy = computed(() => this._orderBy());
    readonly order = computed(() => this._order() as SortDirection);

    readonly isLoading = computed(() => this._baseResource.isLoading());
    readonly isLoadingMore = computed(() => this._isLoadingMore());
    readonly hasMore = computed(() => !!this._nextLink());
    readonly count = computed(() => this._count());
    readonly error = computed(() => this._baseResource.error());
    readonly status = computed(() => this._baseResource.status());
}